import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { PanelModule } from 'primeng/panel';
import { Popover, PopoverModule } from 'primeng/popover';
import { TableModule, TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { DateFormatterPipe } from '../../../pipes/date-formatter.pipe';
import { OrderDetails } from '../order-details.model';

export interface DocumentReference {
  documentId?: `${string} - ${string}`
  fileName?: `${string} - ${string}`
  documentListId?: string
  description?: string
  tagName?: string
}

export interface TableData {
  id: number;
  formattedId: string;
  recordSubject: string;
  providerName: string;
  doctor: string;
  address: string;
  email: string;
  rating?: number;
  recordRequestType: string;
  dateOfServices: string;
  status: string;
  statusMessage: string;
  statusUpdateDate: string,
  clientActionRequired: boolean,
  additionalInfo: {
    isCollapsed: boolean;
    details?: RecordDetails;
  };
  documents?: DocumentReference[];
  [key: string]: any;
}

export interface RecordDetails {
  imaging?: {
    type: 'billing' | string;
    bodyPart: string;
    comments?: string;
  };
  ambulance?: {
    dateOfService: string;
    timeOfService: string;
    pickupAddress: string;
    dropAddress: string;
  };
  employment?: {
    ssn: string;
  };
  insurance?: {
    claimNumber: string;
    policyNumber: string;
  };
  irs?: {
    ssn: string;
  };
  military?: {
    branch?: string;
    serviceNumber?: string;
  };
  socialSecurity?: {
    ssn: string;
    address: string;
  };
  union?: {
    membershipNumber?: string;
  };
  va?: {
    details: string;
  };
  other?: {
    recordDescription: string;
  };
}


@Component({
  selector: 'app-record-index-table',
  imports: [TableModule, ToastModule, ButtonModule, CommonModule, AccordionModule, PanelModule, DropdownModule, FormsModule, PopoverModule, DateFormatterPipe],
  providers: [MessageService],
  templateUrl: './record-index-table.component.html',
  styleUrl: './record-index-table.component.scss'
})
export class RecordIndexTableComponent implements OnChanges {
  @Input() tableData: TableData[] = []
  @Input() isTableLoading: boolean = false;
  @Input() isDocumentsLoading: boolean = false;
  @Input() orderDetails?: OrderDetails;
  @Output() tableAction = new EventEmitter();
  
  totalRecords: number = this.tableData?.length || 0;

   expandedRows: { [key: string]: boolean } = {};

  sortField: string = 'formattedId';
  sortOrder: number = 1;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableData']) {
      this.totalRecords = this.tableData?.length || 0;
      this.applySort();
    }
  }

  expandAll() {
    this.expandedRows = this.tableData?.reduce((acc: { [x: string]: boolean; }, p: { id: string | number; }) => (acc[p.id] = true) && acc, {}) || {};
  }

  collapseAll() {
    this.expandedRows = {};
  }

  onPage(page: any) {
    this.tableAction.emit({ action: 'pageChange', data: page });
  }

  getSeverity(status: string) {
    return 'success';
  }

  getStatusSeverity(status: string) {
    return 'warn';
  }

  onRowExpand(event: TableRowExpandEvent) {

  }

  onRowCollapse(event: TableRowCollapseEvent) {

  }

  toggleRow(rowId: string, expanded: boolean): void {
  // Toggle the expanded state in the expandedRows object using rowId as the key
  this.expandedRows[rowId] = !expanded;
}


  togglePanel(additionalData: any, event: Event) {
    additionalData.isCollapsed = !additionalData.isCollapsed;
    event.stopPropagation(); // Prevents unwanted behavior
  }

  hasDocs(): boolean {
    return this.tableData?.some(request => (request.documents?.length ?? 0) > 0) || false;
  }

  downloadFile(file: DocumentReference) {
    this.tableAction.emit({ action: 'downloadSingleFile', data: file });
  }

  downloadMultipleFiles(files: TableData) {
    this.tableAction.emit({ action: 'downloadMultipleFiles', data: files?.documents?.[0] });
  }

  downloadAllFiles() {
    this.tableAction.emit({ action: 'downloadAllFiles', data: this.tableData.map((data) => data.documents) });
  }

  onSort(event: any) {
    this.sortField = event.field;
    this.sortOrder = event.order;
    this.applySort();
  }

  // Sorts tableData by the active sortField; formattedId uses hierarchical numeric comparison.
  private applySort(): void {
    if (!this.tableData?.length) return;

    this.tableData = [...this.tableData].sort((a, b) => {
      const valueA = this.getSortValue(a[this.sortField], this.sortField);
      const valueB = this.getSortValue(b[this.sortField], this.sortField);

      if (this.sortField === 'formattedId') {
        return this.sortOrder * this.compareHierarchicalId(a[this.sortField], b[this.sortField]);
      }

      return this.sortOrder * (valueA < valueB ? -1 : valueA > valueB ? 1 : 0);
    });
  }

  // Compares request numbers segment-by-segment numerically (e.g. 103116-00 before 103116-002-01).
  private compareHierarchicalId(a: any, b: any): number {
    const toSegments = (v: any) => String(v ?? '').split('-').map(s => parseInt(s, 10) || 0);
    const segsA = toSegments(a);
    const segsB = toSegments(b);
    const len = Math.max(segsA.length, segsB.length);
    for (let i = 0; i < len; i++) {
      const diff = (segsA[i] ?? 0) - (segsB[i] ?? 0);
      if (diff !== 0) return diff;
    }
    return 0;
  }

  private getSortValue(value: any, field: string): string | number {
    if (!value) return -Infinity;
    
    if (field === 'formattedId') {
      // Handle hierarchical request numbers (e.g., "001", "001-01", "001-02")
      const parts = (value as string).split('-');
      // Guard: only parse last segment if it is a valid integer
      const last = parts[parts.length - 1];
      const num = parseInt(last, 10);
      return isNaN(num) ? -Infinity : num;
    }
    
    if (!isNaN(Number(value))) {
      return Number(value);
    }
    
    if (typeof value === 'string') {
      return value.toLowerCase();
    }
    
    return value.toString().toLowerCase();
  }

  detailsHasKeys(additionalDetails: any): boolean {
    return additionalDetails && Object.keys(additionalDetails).length > 0;
  }

}
